import { Component, Inject, Input, OnChanges, OnInit, Optional, SimpleChanges, ViewChild } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { FilterService } from '@app/core/services/container/filter.service';
import { Constants } from '@app/shared/constants';
import { Entity } from '@app/shared/models/models.interface';
import { SortParam } from '@app/shared/models/page.interface';
import { FilterBuilderService } from '@app/shared/services/filter-builder.service';
import { StateService } from '@app/shared/services/state-service.interface';
import { Column, DataTableState } from '../data-table.component';
import { TableDataSource } from '../data-table.datasource';

@Component({
  selector: 'vfs-base-view',
  templateUrl: './base-view.component.html',
  styleUrls: ['./base-view.component.scss']
})
/**
 * This component is used by the grid and card views as base to share some
 * common tasks and properties.
 */
export class BaseViewComponent<T extends Entity, S = any> implements OnInit, OnChanges {
  @Input() dataSource: TableDataSource<T, S>;
  @Input() sortColumn = '';
  @Input() sortDirection = Constants.ASC_DIR;
  @Input() columns: Column[] = [];
  @Input() searchValue = '';
  @ViewChild(MatPaginator) public paginator: MatPaginator;
  @ViewChild(MatSort) public sort: MatSort;
  @Input() service: StateService<T, S> = null;
  @Input() id: string;

  /**
   * Constructor
   *
   * @param filterService the filterService
   */
  constructor(
    protected filterService: FilterService,
    @Optional() @Inject('FilterBuilderService') protected filterBuilderService: FilterBuilderService
  ) {}

  /**
   * Initialize the component
   */
  ngOnInit() {}

  /**
   * Load a page applying filters, sorting, etc.
   */
  loadPage() {
    let currentSort: SortParam = { field: '', direction: '' };
    if (this.sort) {
      //The child supports sorting (e.g. grid, list)
      currentSort.direction = this.sort.direction ? this.sort.direction : Constants.ASC_DIR;
      currentSort.field = this.sort.active;
      if (this.service && this.service.getDataSourceState()) {
        let dataTableState: DataTableState = this.getDataTableState();
        dataTableState.currentIndex = this.paginator.pageIndex;
        dataTableState.sort = currentSort;
        this.service.saveState();
      }
    } else {
      //The child doesn't support sorting "directly" (e.g. card). Usually the sorting is in the
      //datatable state
      currentSort.direction = Constants.ASC_DIR;
      currentSort.field = this.sortColumn;
      if (this.service && this.service.getDataSourceState()) {
        let dataTableState: DataTableState = this.getDataTableState();
        if (dataTableState.sort) {
          currentSort = dataTableState.sort;
        }
      }
    }

    this.dataSource.getAll(
      this.paginator.pageIndex.toString(),
      this.paginator.pageSize.toString(),
      [{ field: currentSort.field, direction: currentSort.direction }],
      this.buildFilter(this.searchValue || '')
    );
  }

  /**
   * Build the filters as expected by the server side
   *
   * @param value filter value
   * @return the filters
   */
  buildFilter(value: string): string {
    var fs = null;
    if (this.service.getFilterState) {
      fs = this.service.getFilterState(this.id);
    }
    return this.filterBuilderService
      ? this.filterBuilderService.build(value, this.columns, fs)
      : this.filterService.buildFilterForSearch(value, this.columns, fs);
  }

  /**
   * Changes on search must be applied to show the results
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.searchValue) {
      if (changes.searchValue.previousValue !== undefined) {
        this.loadPage();
      }
    }
  }

  /**
   * Return the page index
   */
  getPageIndex(): number {
    if (this.service) {
      return this.getDataTableState().currentIndex;
    } else {
      return 0;
    }
  }

  /**
   * Return the number of items x page
   */
  getPageSize(): number {
    if (this.service) {
      return this.getDataTableState().currentSize;
    } else {
      return 10;
    }
  }

  /**
   * Manages the paginator changes
   *
   * @param event an instance of PageEvent class
   */
  pageChange(event: PageEvent) {
    if (this.service && this.service.getDataSourceState(this.id)) {
      this.getDataTableState().currentIndex = event.pageIndex;
      this.getDataTableState().currentSize = event.pageSize;
      this.service.saveState();
    }
  }
  /**
   * Get the datatable state
   */
  private getDataTableState(): DataTableState {
    return this.service.getDataTableState(this.id);
  }
}
